import { supabase } from '@/lib/supabase/client';
import type { Collection, Fabric } from '@/types/models';

/**
 * Fetch all collections with optional filtering
 */
export async function getCollections(filters?: {
  fabricOrigin?: string;
  style?: string;
  search?: string;
}): Promise<Collection[]> {
  try {
    let query = supabase
      .from('collections')
      .select('*')
      .order('created_at', { ascending: false });

    // Apply fabric origin filter
    if (filters?.fabricOrigin && filters.fabricOrigin !== 'all') {
      query = query.eq('fabric_origin', filters.fabricOrigin);
    }

    // Apply style filter
    if (filters?.style && filters.style !== 'all') {
      query = query.eq('style', filters.style);
    }

    // Apply search filter
    if (filters?.search) {
      query = query.or(
        `title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,origin_detail.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching collections:', error);
      throw error;
    }

    // Map database fields to frontend interface
    return (data || []).map((collection) => ({
      id: collection.id,
      title: collection.title,
      description: collection.description,
      image: collection.image_url,
      alt: collection.image_alt,
      fabricCount: 0, // Will be populated separately if needed
      fabricOrigin: collection.fabric_origin as 'Indian' | 'Imported',
      originDetail: collection.origin_detail || undefined,
      category: collection.category,
      style: collection.style,
    }));
  } catch (error) {
    console.error('Error in getCollections:', error);
    return [];
  }
}

/**
 * Fetch a single collection by ID
 */
export async function getCollectionById(id: string): Promise<Collection | null> {
  try {
    const { data, error } = await supabase
      .from('collections')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching collection:', error);
      throw error;
    }

    if (!data) return null;

    return {
      id: data.id,
      title: data.title,
      description: data.description,
      image: data.image_url,
      alt: data.image_alt,
      fabricCount: 0, // Will be populated separately
      fabricOrigin: data.fabric_origin as 'Indian' | 'Imported',
      originDetail: data.origin_detail || undefined,
      category: data.category,
      style: data.style,
    };
  } catch (error) {
    console.error('Error in getCollectionById:', error);
    return null;
  }
}

/**
 * Fetch fabrics for a specific collection
 */
export async function getFabricsByCollection(collectionId: string): Promise<Fabric[]> {
  try {
    const { data, error } = await supabase
      .from('fabrics')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('available', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching fabrics:', error);
      throw error;
    }

    return (data || []).map((fabric) => ({
      id: fabric.id,
      collectionId: fabric.collection_id,
      name: fabric.name,
      description: fabric.description || '',
      fabricType: fabric.fabric_type,
      composition: fabric.composition || '',
      weight: fabric.weight || '',
      width: fabric.width || '',
      careInstructions: fabric.care_instructions || '',
      color: fabric.color || '',
      pattern: fabric.pattern || '',
      texture: fabric.texture || '',
      imageUrl: fabric.image_url,
      imageAlt: fabric.image_alt,
      priceRange: fabric.price_range || '',
      available: fabric.available,
    }));
  } catch (error) {
    console.error('Error in getFabricsByCollection:', error);
    return [];
  }
}

/**
 * Get fabric count for a specific collection
 */
export async function getCollectionFabricCount(collectionId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('fabrics')
      .select('*', { count: 'exact', head: true })
      .eq('collection_id', collectionId)
      .eq('available', true);

    if (error) {
      console.error('Error fetching fabric count:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error in getCollectionFabricCount:', error);
    return 0;
  }
}

/**
 * Get collections with fabric counts
 */
export async function getCollectionsWithCounts(filters?: {
  fabricOrigin?: string;
  style?: string;
  search?: string;
}): Promise<Collection[]> {
  try {
    const collections = await getCollections(filters);
    
    // Fetch fabric counts for all collections
    const collectionsWithCounts = await Promise.all(
      collections.map(async (collection) => {
        const fabricCount = await getCollectionFabricCount(collection.id);
        return {
          ...collection,
          fabricCount,
        };
      })
    );

    return collectionsWithCounts;
  } catch (error) {
    console.error('Error in getCollectionsWithCounts:', error);
    return [];
  }
}

/**
 * Search fabrics across all collections
 */
export async function searchFabrics(searchTerm: string): Promise<Fabric[]> {
  try {
    const { data, error } = await supabase
      .from('fabrics')
      .select('*')
      .or(
        `name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,fabric_type.ilike.%${searchTerm}%,color.ilike.%${searchTerm}%`
      )
      .eq('available', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error searching fabrics:', error);
      throw error;
    }

    return (data || []).map((fabric) => ({
      id: fabric.id,
      collectionId: fabric.collection_id,
      name: fabric.name,
      description: fabric.description || '',
      fabricType: fabric.fabric_type,
      composition: fabric.composition || '',
      weight: fabric.weight || '',
      width: fabric.width || '',
      careInstructions: fabric.care_instructions || '',
      color: fabric.color || '',
      pattern: fabric.pattern || '',
      texture: fabric.texture || '',
      imageUrl: fabric.image_url,
      imageAlt: fabric.image_alt,
      priceRange: fabric.price_range || '',
      available: fabric.available,
    }));
  } catch (error) {
    console.error('Error in searchFabrics:', error);
    return [];
  }
}

/**
 * Fetch all fabrics with optional filtering for Fabric Library page
 */
export async function getAllFabrics(filters?: {
  fabricType?: string;
  fabricOrigin?: string;
  search?: string;
}): Promise<Fabric[]> {
  try {
    // First, get all fabrics
    let fabricQuery = supabase
      .from('fabrics')
      .select('*, collections!inner(fabric_origin)')
      .eq('available', true)
      .order('name', { ascending: true });

    // Apply fabric type filter
    if (filters?.fabricType && filters.fabricType !== 'all') {
      fabricQuery = fabricQuery.eq('fabric_type', filters.fabricType);
    }

    // Apply search filter
    if (filters?.search) {
      fabricQuery = fabricQuery.or(
        `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%,fabric_type.ilike.%${filters.search}%,color.ilike.%${filters.search}%`
      );
    }

    const { data: fabricsData, error: fabricsError } = await fabricQuery;

    if (fabricsError) {
      throw fabricsError;
    }

    // Map and filter by fabric origin if needed
    let fabrics = (fabricsData || []).map((fabric) => ({
      id: fabric.id,
      collectionId: fabric.collection_id,
      name: fabric.name,
      description: fabric.description || '',
      fabricType: fabric.fabric_type,
      composition: fabric.composition || '',
      weight: fabric.weight || '',
      width: fabric.width || '',
      careInstructions: fabric.care_instructions || '',
      color: fabric.color || '',
      pattern: fabric.pattern || '',
      texture: fabric.texture || '',
      imageUrl: fabric.image_url,
      imageAlt: fabric.image_alt,
      priceRange: fabric.price_range || '',
      available: fabric.available,
      fabricOrigin: fabric.collections?.fabric_origin || 'Indian',
    }));

    // Apply fabric origin filter after fetching
    if (filters?.fabricOrigin && filters.fabricOrigin !== 'all') {
      fabrics = fabrics.filter(
        (fabric) => fabric.fabricOrigin === filters.fabricOrigin
      );
    }

    return fabrics;
  } catch (error) {
    console.error('Error in getAllFabrics:', error);
    return [];
  }
}

// User Favorites Functions

/**
 * Add fabric to user favorites
 */
export async function addFabricToFavorites(fabricId: string, collectionName?: string, notes?: string) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated to add favorites');
  }

  const { data, error } = await supabase
    .from('user_fabric_favorites')
    .insert({
      user_id: user.id,
      fabric_id: fabricId,
      collection_name: collectionName,
      notes: notes
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Remove fabric from user favorites
 */
export async function removeFabricFromFavorites(fabricId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated to remove favorites');
  }

  const { error } = await supabase
    .from('user_fabric_favorites')
    .delete()
    .eq('user_id', user.id)
    .eq('fabric_id', fabricId);

  if (error) throw error;
}

/**
 * Get user's favorite fabrics with details
 */
export async function getUserFavoriteFabrics() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .rpc('get_user_favorite_fabrics', { p_user_id: user.id });

  if (error) throw error;
  return data || [];
}

/**
 * Check if fabric is in user favorites
 */
export async function isFabricFavorited(fabricId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return false;
  }

  const { data, error } = await supabase
    .rpc('is_fabric_favorited', { 
      p_user_id: user.id,
      p_fabric_id: fabricId 
    });

  if (error) {
    console.error('Error checking favorite status:', error);
    return false;
  }
  
  return data || false;
}

/**
 * Update favorite fabric notes or collection
 */
export async function updateFabricFavorite(
  fabricId: string, 
  updates: { collectionName?: string; notes?: string }
) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated to update favorites');
  }

  const { data, error } = await supabase
    .from('user_fabric_favorites')
    .update({
      collection_name: updates.collectionName,
      notes: updates.notes
    })
    .eq('user_id', user.id)
    .eq('fabric_id', fabricId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Preference Profiles Functions

/**
 * Create user preference profile
 */
export async function createPreferenceProfile(profile: {
  profileName: string;
  roomType?: string;
  stylePreference?: string;
  colorScheme?: string;
  fabricTypes?: string[];
  budgetRange?: string;
  lightingPreference?: string;
  isDefault?: boolean;
}) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated to create preference profiles');
  }

  const { data, error } = await supabase
    .from('user_preference_profiles')
    .insert({
      user_id: user.id,
      profile_name: profile.profileName,
      room_type: profile.roomType,
      style_preference: profile.stylePreference,
      color_scheme: profile.colorScheme,
      fabric_types: profile.fabricTypes,
      budget_range: profile.budgetRange,
      lighting_preference: profile.lightingPreference,
      is_default: profile.isDefault || false
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get user preference profiles
 */
export async function getUserPreferenceProfiles() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_preference_profiles')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Update preference profile
 */
export async function updatePreferenceProfile(
  profileId: string,
  updates: Partial<{
    profileName: string;
    roomType: string;
    stylePreference: string;
    colorScheme: string;
    fabricTypes: string[];
    budgetRange: string;
    lightingPreference: string;
    isDefault: boolean;
  }>
) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated to update profiles');
  }

  const updateData: any = {};
  if (updates.profileName !== undefined) updateData.profile_name = updates.profileName;
  if (updates.roomType !== undefined) updateData.room_type = updates.roomType;
  if (updates.stylePreference !== undefined) updateData.style_preference = updates.stylePreference;
  if (updates.colorScheme !== undefined) updateData.color_scheme = updates.colorScheme;
  if (updates.fabricTypes !== undefined) updateData.fabric_types = updates.fabricTypes;
  if (updates.budgetRange !== undefined) updateData.budget_range = updates.budgetRange;
  if (updates.lightingPreference !== undefined) updateData.lighting_preference = updates.lightingPreference;
  if (updates.isDefault !== undefined) updateData.is_default = updates.isDefault;

  const { data, error } = await supabase
    .from('user_preference_profiles')
    .update(updateData)
    .eq('id', profileId)
    .eq('user_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete preference profile
 */
export async function deletePreferenceProfile(profileId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated to delete profiles');
  }

  const { error } = await supabase
    .from('user_preference_profiles')
    .delete()
    .eq('id', profileId)
    .eq('user_id', user.id);

  if (error) throw error;
}

// Comparison Shortcuts Functions

/**
 * Save fabric comparison
 */
export async function saveFabricComparison(
  comparisonName: string,
  fabricIds: string[],
  notes?: string
) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated to save comparisons');
  }

  const { data, error } = await supabase
    .from('user_fabric_comparisons')
    .insert({
      user_id: user.id,
      comparison_name: comparisonName,
      fabric_ids: fabricIds,
      notes: notes
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get user saved comparisons
 */
export async function getUserFabricComparisons() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_fabric_comparisons')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

/**
 * Delete saved comparison
 */
export async function deleteFabricComparison(comparisonId: string) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated to delete comparisons');
  }

  const { error } = await supabase
    .from('user_fabric_comparisons')
    .delete()
    .eq('id', comparisonId)
    .eq('user_id', user.id);

  if (error) throw error;
}